import React, { useState, Fragment, useEffect } from 'react';
import PhoneInput from 'react-phone-input-2';
import CircularProgress from '@mui/material/CircularProgress';
import Checkbox from '@mui/material/Checkbox';
import { useSelector, useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import cn from 'classnames';
import Cookies from '@ictlife/core/config/cookies';

import SnackInfo from '@ictlife/core/components/SnackInfo';
import generateUsernameSuggestions, {
  checkUsernameAvailability,
} from '@ictlife/core/utils/generateUsernameSuggestions';
import useDebouncedEffect from '@ictlife/core/hooks/useDebouncedEffect';
import { sendVerificationCode } from '../../state/actions/authenticationAction';

import styles from './styles/IdentificationScreen.module.scss';
import GoogleLogin from './GoogleLogin';
import { redirectLoggedInUser } from './storeAndRedirectUser';

const IdentificationScreen = () => {
  const dispatch = useDispatch();
  const router = useRouter();

  useEffect(() => {
    if (Cookies.get('ictl_token')) {
      const redirect = router.query.redirect;
      redirectLoggedInUser({ router, redirect, dispatch });
    }
  }, [dispatch, router]);

  const [snackInfo, setSnackInfo] = useState({
    open: false,
    message: '',
    vertical: 'bottom',
    horizontal: 'left',
    severity: 'info',
    autoHideDuration: 5000,
  });

  const [termsAccepted, setTermsAccepted] = useState(false);

  const defaultUserInfo =
    typeof window !== 'undefined' && localStorage.userInfo
      ? JSON.parse(localStorage.userInfo)
      : undefined;

  const [phoneValue, setPhoneValue] = useState({
    dialCode: defaultUserInfo?.phone_number.country_code,
    localValue: defaultUserInfo?.phone_number.number,
    internationalValue: `${defaultUserInfo?.phone_number.country_code || ''}${
      defaultUserInfo?.phone_number.number || ''
    }`,
  });

  const [draftUserInfo, setDraftUserInfo] = useState(defaultUserInfo || {});

  const { dialCode, localValue, internationalValue } = phoneValue;

  const verificationCodeState = useSelector(
    store => store.verificationCodeState
  );

  if (
    verificationCodeState.isError &&
    snackInfo.message !== verificationCodeState.errorMessage
  ) {
    setSnackInfo(snackInfo => ({
      ...snackInfo,
      open: true,
      message: verificationCodeState.errorMessage,
      severity: 'error',
      autoHideDuration: null,
    }));
    dispatch({ type: 'RESET_VERIFICATION_CODE_MESSAGES' });
  }

  const [usernameSuggestions, setUsernameSuggestions] = useState([]);
  const [isUsernameAvailable, setIsUsernameAvailable] = useState({
    loading: false,
    available: defaultUserInfo?.username && true,
  });

  const handleGenerateUsernameSuggestions = useDebouncedEffect(
    ({ currentUserInfo, phone }) => {
      generateUsernameSuggestions({
        phone: phone,
        name:
          currentUserInfo.fname &&
          currentUserInfo.lname &&
          `${currentUserInfo.fname} ${currentUserInfo.lname}`,
        isInterchangeable: true,
      }).then(suggestionsList => setUsernameSuggestions(suggestionsList));
    },
    1000
  );

  const handlePhoneChange = (value, data) => {
    let rawPhoneValue = value
      .replace(/[^0-9]+/g, '')
      .slice(data.dialCode.length);
    setPhoneValue({
      dialCode: data.dialCode,
      localValue: rawPhoneValue,
      internationalValue: value,
    });

    handleGenerateUsernameSuggestions({
      currentUserInfo: userInfo,
      phone: value,
    });
  };

  const userInfo = {
    ...draftUserInfo,
    phone_number: {
      country_code: dialCode,
      number: localValue,
    },
  };

  const handleSendVerificationCode = e => {
    e.preventDefault();
    sendVerificationCode(dispatch, userInfo).then(() => {
      localStorage.userInfo = JSON.stringify(userInfo);
      const redirect = router.query.redirect;
      if (redirect) {
        router.push(`/user/confirmotp?redirect=${redirect}`);
      } else {
        router.push('/user/confirmotp');
      }
    });
  };

  const isDataInValid =
    !internationalValue ||
    (userInfo.confirm_password &&
      userInfo.password &&
      userInfo.password !== userInfo.confirm_password) ||
    !userInfo.fname ||
    !userInfo.lname ||
    !isUsernameAvailable.available;

  const handleInputChange = e => {
    setDraftUserInfo(info => {
      const updatedUserInfo = { ...info, [e.target.name]: e.target.value };
      handleGenerateUsernameSuggestions({
        currentUserInfo: updatedUserInfo,
        phone: internationalValue,
      });
      return updatedUserInfo;
    });
  };

  const handleCheckUsernameAvailability = useDebouncedEffect(username => {
    setIsUsernameAvailable(available => ({ ...available, loading: true }));
    checkUsernameAvailability({ username }).then(data => {
      setIsUsernameAvailable({
        available: data.available,
        loading: false,
        message: data.error_message,
      });
    });
  }, 700);

  const handleUsernameChange = e => {
    const username = e.target.value;
    setDraftUserInfo(info => ({
      ...info,
      username,
    }));

    if (username.length > 2) {
      handleCheckUsernameAvailability(e.target.value);
    } else if (username.length > 0) {
      setIsUsernameAvailable({
        available: null,
        loading: false,
        message: 'too short. Type in more characters',
      });
    } else {
      setIsUsernameAvailable({
        available: null,
        loading: false,
      });
    }
  };

  return (
    <Fragment>
      <GoogleLogin text="Sign Up With Google" />
      <div className="text-center mb-4 font-semibold">OR</div>
      <div className={styles.identificationFormWrapper}>
        <form
          className={cn(
            'app-form',
            styles.formControls,
            styles.phoneInputWrapper
          )}
          onSubmit={handleSendVerificationCode}
        >
          <div className="column-input">
            <div className="input-group">
              <label htmlFor="fname">First Name</label>
              <input
                id="fname"
                name="fname"
                defaultValue={draftUserInfo.fname}
                required
                onChange={handleInputChange}
              />
            </div>
            <div className="input-group">
              <label htmlFor="lname">Last Name</label>
              <input
                id="lname"
                name="lname"
                defaultValue={draftUserInfo.lname}
                required
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="input-group">
            <label htmlFor="phone">Phone</label>

            <PhoneInput
              country="ke"
              enableSearch
              countryCodeEditable={false}
              value={internationalValue}
              onChange={(value, data) => handlePhoneChange(value, data)}
            />
            <div className="helper-text">
              Used to verify and recover your account
            </div>
          </div>

          <div className="input-group">
            <div className="label-group">
              <label htmlFor="username">Username</label>

              {isUsernameAvailable.loading ? (
                <CircularProgress size={15} className="button-loader" />
              ) : (
                <Fragment>
                  {isUsernameAvailable.available && (
                    <span className={cn('material-icons', 'success-icon')}>
                      &#xe5ca;
                    </span>
                  )}
                  {isUsernameAvailable.available !== undefined &&
                    !isUsernameAvailable.available && (
                      <span className={cn('material-icons', 'danger-icon')}>
                        &#xe14c;
                      </span>
                    )}
                </Fragment>
              )}
            </div>
            <input
              id="username"
              name="username"
              type="tel"
              autoComplete="username"
              value={userInfo.username || ''}
              placeholder="select from suggestions below or type your choice"
              required
              onChange={handleUsernameChange}
            />
            {usernameSuggestions.length > 0 && (
              <div className="username-suggestions">
                <span>suggestions: </span>
                {usernameSuggestions.map(username => (
                  <>
                    <span
                      className="suggestion"
                      key={username}
                      onClick={() => {
                        handleCheckUsernameAvailability(username);
                        setDraftUserInfo(info => ({
                          ...info,
                          username,
                        }));
                      }}
                    >
                      {username}
                    </span>
                    {', '}
                  </>
                ))}
              </div>
            )}
            {isUsernameAvailable.message ? (
              <div className="helper-text error-section">
                {isUsernameAvailable.message}
              </div>
            ) : (
              <div className="helper-text">
                Used for login. Use something you can easily remember
              </div>
            )}
          </div>

          <div className="column-input">
            <div className="input-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                name="password"
                type="password"
                defaultValue={draftUserInfo.password}
                autoComplete="new-password"
                required
                onChange={e =>
                  setDraftUserInfo(info => ({
                    ...info,
                    password: e.target.value,
                  }))
                }
              />
              <div className="helper-text error-section">
                {userInfo.password &&
                  userInfo.password.length < 8 &&
                  'use a minimum of 8 characters'}
                <span style={{ visibility: 'hidden' }}>pass</span>
              </div>
            </div>
            <div className="input-group">
              <label htmlFor="cpassword">Confirm Password</label>
              <input
                id="cpassword"
                required
                name="cpassword"
                autoComplete="new-password"
                defaultValue={draftUserInfo.confirm_password}
                type="password"
                onChange={e =>
                  setDraftUserInfo(info => ({
                    ...info,
                    confirm_password: e.target.value,
                  }))
                }
              />
              <div className="helper-text error-section">
                {userInfo.confirm_password &&
                  userInfo.password &&
                  userInfo.password !== userInfo.confirm_password &&
                  'passwords not matching'}
                <span style={{ visibility: 'hidden' }}>pass</span>
              </div>
            </div>
          </div>

          <div className={cn(styles.agreeTerms, 'override')}>
            <Checkbox
              id="terms-checkbox"
              color="primary"
              checked={termsAccepted}
              onChange={() => setTermsAccepted(accept => !accept)}
              onKeyDown={e => {
                if (e.keyCode === 13) {
                  e.preventDefault();
                  if (!termsAccepted) {
                    setTermsAccepted(accept => !accept);
                  }
                }
              }}
            />
            <span className={styles.termsMessage}>
              I agree to ICTLife{' '}
              <span onClick={() => router.push('/terms')}>
                Terms of Service
              </span>{' '}
              and{' '}
              <span onClick={() => router.push('/policy')}>Privacy Policy</span>
            </span>
          </div>

          <div
            className="button"
            style={{ display: 'grid', justifyContent: 'center' }}
          >
            <button
              id="send-verification-button"
              className="custom-button"
              disabled={
                verificationCodeState.isLoading === true ||
                isDataInValid ||
                !termsAccepted
              }
            >
              Verify Phone Number
            </button>
            {verificationCodeState.isLoading && (
              <CircularProgress size={20} className="button-loader" />
            )}
          </div>
        </form>
      </div>
      <SnackInfo
        snackInfo={snackInfo}
        handleClose={() =>
          setSnackInfo(snackInfo => ({
            ...snackInfo,
            message: '',
            open: false,
          }))
        }
      />
    </Fragment>
  );
};

export default IdentificationScreen;
